import ErrorComponent from "Components/ErrorComponent";
import ReportDetailsHeader from "Components/ReportDetailsHeader";
import {
    commonValidationMessages,
    WeatherDataValidationMessages,
} from "Components/ValidationErrorMessages";
import { useDocumentTitle } from "Components/useDocument.hooks";
import {
    calculateTimeDurationBetwnReports,
    checkValidArray,
    checkValidObject,
    dynamicSetFieldErrors,
    handleServerResponse,
    isFormUnchanged,
    useSafeFormikContext,
} from "GenericForms/Helper";
import {
    Get,
} from "VesselMaster/vesselMaster.hooks";
import env from "environment_system/env_system";
import apiGlobal from "global/api.global";
import React, { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { queryClient } from "react-query/queryClient";
import {
    Row,
    Col,
    Card,
    CardHeader,
} from "reactstrap";
import { AddInfoConstant, ReportingEvents } from "shared/constants";
import { queryKeyes } from "shared/queryKeys";
import * as Yup from "yup";
import WeatherDataStructure from "./WeatherDataStructure";
import { Formik } from "formik";
import { RootState } from "index";
import { useDispatch, useSelector } from "react-redux";
import { getCurrentReportAction, getCurrentReportFilesAction } from "Store/Generic/ReportingSlice";
import FormLoading from "Components/FormLoading";

interface WeatherDataType {
    toggleTab?: any;
    setErrorMessage?: any;
}

const WeatherData = ({
    toggleTab,
    setErrorMessage,
}: WeatherDataType) => {
    /** State variables start*/
    useDocumentTitle("Weather Data Report - ecoSAIL");
    const { VesselID, ReportID, VoyageID, CurrentReport, PreviousReport, reportingLoading, reportingError } = useSelector((state: RootState) => state.Reporting);
    const dispatch = useDispatch();
    const [alertBool, setAlertBool] = useState<boolean>(false);
    const [alertColourType, setAlertColourType] = useState<string>(null);
    const [alertType, setAlertType] = useState<string>(null);
    const [alertMessage, setAlertMessage] = useState<string>(null);
    const [fieldLabel, setAlertFieldLabel] = useState<string>(null);
    const [fieldValue, setAlertFieldValue] = useState<any>(null);
    const [fieldId, setFieldId] = useState<string>(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const AddGeneralAvgSpeed = env.additional_info_deck === AddInfoConstant.PENISULAR_DECK;
    const formikContext = useSafeFormikContext<any>();
    const editValues = formikContext?.values;
    const setEditValues = formikContext?.setValues;
    const editInitialValues = formikContext?.initialValues;
    const editSubmit = formikContext?.submitForm;
    const editIsSubmitting = formikContext?.isSubmitting;
    const weatherDataCurrentReport = CurrentReport?.data?.deck_officer?.weather_data ?? null;
    const currentVesselReport = CurrentReport?.data?.vessel_reporting_information ?? null;
    const previousVesselReport = PreviousReport?.data?.vessel_reporting_information ?? null;
    /** State variables end*/

    /** Fields' visibility logic */
    const fieldVisibility: any = {
        steamingTime: [
            ReportingEvents.NOON_RFA_AS,
            ReportingEvents.EOSP,
            ReportingEvents.HASP,
            ReportingEvents.CII_ADJUSTMENT_AS,
        ],
        avgGroundSpeed: [
            ReportingEvents.NOON_RFA_AS,
            ReportingEvents.EOSP,
            ReportingEvents.HASP,
            ReportingEvents.CII_ADJUSTMENT_AS,
        ],
        generalAvgSpeed: [
            ReportingEvents.NOON_RFA_AS,
            ReportingEvents.EOSP,
            ReportingEvents.HASP,
            ReportingEvents.CII_ADJUSTMENT_AS,
        ],
        avgWaterSpeed: [
            ReportingEvents.DEPARTURE_FROM_BERTH,
            ReportingEvents.HEAVE_ANCHOR,
            ReportingEvents.DEPARTURE_FROM_DRIFTING_IP,
            ReportingEvents.DEPARTURE_FROM_DRIFTING_AS,
            ReportingEvents.CII_ADJUSTMENT_IP,
        ],
        windDirection: [
            ReportingEvents.NOON_RFA_AS,
            ReportingEvents.EOSP,
            ReportingEvents.HASP,
            ReportingEvents.AT_BERTH,
            ReportingEvents.DEPARTURE_FROM_BERTH,
            ReportingEvents.NOON_FWE_IP,
            ReportingEvents.DROP_ANCHOR,
            ReportingEvents.DRIFTING_FWE_IP,
            ReportingEvents.DRIFTING_SBE_IP,
            ReportingEvents.DEPARTURE_FROM_DRIFTING_IP,
            ReportingEvents.HEAVE_ANCHOR,
            ReportingEvents.NOON_SBE_IP,
            ReportingEvents.BOSP,
            ReportingEvents.DRIFTING_SBE_AS,
            ReportingEvents.DRIFTING_FWE_AS,
            ReportingEvents.DEPARTURE_FROM_DRIFTING_AS,
            ReportingEvents.ROSP,
            ReportingEvents.NOON_FWE_AS,
            ReportingEvents.NOON_SBE_AS,
            ReportingEvents.CII_ADJUSTMENT_AS,
            ReportingEvents.CII_ADJUSTMENT_IP,
        ],
        windSpeed: [
            ReportingEvents.NOON_RFA_AS,
            ReportingEvents.EOSP,
            ReportingEvents.HASP,
            ReportingEvents.AT_BERTH,
            ReportingEvents.DEPARTURE_FROM_BERTH,
            ReportingEvents.NOON_FWE_IP,
            ReportingEvents.DROP_ANCHOR,
            ReportingEvents.DRIFTING_FWE_IP,
            ReportingEvents.DRIFTING_SBE_IP,
            ReportingEvents.DEPARTURE_FROM_DRIFTING_IP,
            ReportingEvents.HEAVE_ANCHOR,
            ReportingEvents.NOON_SBE_IP,
            ReportingEvents.BOSP,
            ReportingEvents.DRIFTING_SBE_AS,
            ReportingEvents.DRIFTING_FWE_AS,
            ReportingEvents.DEPARTURE_FROM_DRIFTING_AS,
            ReportingEvents.ROSP,
            ReportingEvents.NOON_FWE_AS,
            ReportingEvents.NOON_SBE_AS,
            ReportingEvents.CII_ADJUSTMENT_AS,
            ReportingEvents.CII_ADJUSTMENT_IP,
        ],
        beaufortScale: [
            ReportingEvents.NOON_RFA_AS,
            ReportingEvents.EOSP,
            ReportingEvents.HASP,
            ReportingEvents.AT_BERTH,
            ReportingEvents.DEPARTURE_FROM_BERTH,
            ReportingEvents.NOON_FWE_IP,
            ReportingEvents.DROP_ANCHOR,
            ReportingEvents.DRIFTING_FWE_IP,
            ReportingEvents.DRIFTING_SBE_IP,
            ReportingEvents.DEPARTURE_FROM_DRIFTING_IP,
            ReportingEvents.HEAVE_ANCHOR,
            ReportingEvents.NOON_SBE_IP,
            ReportingEvents.BOSP,
            ReportingEvents.DRIFTING_SBE_AS,
            ReportingEvents.DRIFTING_FWE_AS,
            ReportingEvents.DEPARTURE_FROM_DRIFTING_AS,
            ReportingEvents.ROSP,
            ReportingEvents.NOON_FWE_AS,
            ReportingEvents.NOON_SBE_AS,
            ReportingEvents.CII_ADJUSTMENT_AS,
            ReportingEvents.CII_ADJUSTMENT_IP,
        ],
        swellDirection: [
            ReportingEvents.NOON_FWE_IP,
            ReportingEvents.NOON_SBE_IP,
            ReportingEvents.NOON_FWE_AS,
            ReportingEvents.NOON_SBE_AS,
            ReportingEvents.DROP_ANCHOR,
            ReportingEvents.HEAVE_ANCHOR,
            ReportingEvents.DRIFTING_FWE_IP,
            ReportingEvents.DRIFTING_SBE_IP,
            ReportingEvents.DEPARTURE_FROM_DRIFTING_IP,
            ReportingEvents.BOSP,
            ReportingEvents.DEPARTURE_FROM_DRIFTING_AS,
            ReportingEvents.NOON_RFA_AS,
            ReportingEvents.HASP,
            ReportingEvents.DRIFTING_SBE_AS,
            ReportingEvents.DRIFTING_FWE_AS,
            ReportingEvents.ROSP,
            ReportingEvents.CII_ADJUSTMENT_AS,
            ReportingEvents.CII_ADJUSTMENT_IP,
        ],
        swellHeight: [
            ReportingEvents.NOON_RFA_AS,
            ReportingEvents.EOSP,
            ReportingEvents.HASP,
            ReportingEvents.NOON_FWE_IP,
            ReportingEvents.DROP_ANCHOR,
            ReportingEvents.DRIFTING_FWE_IP,
            ReportingEvents.DRIFTING_SBE_IP,
            ReportingEvents.DEPARTURE_FROM_DRIFTING_IP,
            ReportingEvents.HEAVE_ANCHOR,
            ReportingEvents.NOON_SBE_IP,
            ReportingEvents.BOSP,
            ReportingEvents.DRIFTING_SBE_AS,
            ReportingEvents.DRIFTING_FWE_AS,
            ReportingEvents.DEPARTURE_FROM_DRIFTING_AS,
            ReportingEvents.ROSP,
            ReportingEvents.NOON_FWE_AS,
            ReportingEvents.NOON_SBE_AS,
            ReportingEvents.CII_ADJUSTMENT_AS,
            ReportingEvents.CII_ADJUSTMENT_IP,
        ],
        oceanCurrentSpeed: [
            ReportingEvents.NOON_RFA_AS,
            ReportingEvents.EOSP,
            ReportingEvents.HASP,
            ReportingEvents.NOON_FWE_IP,
            ReportingEvents.DROP_ANCHOR,
            ReportingEvents.DRIFTING_FWE_IP,
            ReportingEvents.DRIFTING_SBE_IP,
            ReportingEvents.DEPARTURE_FROM_DRIFTING_IP,
            ReportingEvents.HEAVE_ANCHOR,
            ReportingEvents.NOON_SBE_IP,
            ReportingEvents.BOSP,
            ReportingEvents.DRIFTING_SBE_AS,
            ReportingEvents.DRIFTING_FWE_AS,
            ReportingEvents.DEPARTURE_FROM_DRIFTING_AS,
            ReportingEvents.ROSP,
            ReportingEvents.NOON_FWE_AS,
            ReportingEvents.NOON_SBE_AS,
            ReportingEvents.CII_ADJUSTMENT_AS,
            ReportingEvents.CII_ADJUSTMENT_IP,
        ],
        oceanWaveHeight: [
            ReportingEvents.NOON_RFA_AS,
            ReportingEvents.EOSP,
            ReportingEvents.HASP,
            ReportingEvents.NOON_FWE_IP,
            ReportingEvents.DROP_ANCHOR,
            ReportingEvents.DRIFTING_FWE_IP,
            ReportingEvents.DRIFTING_SBE_IP,
            ReportingEvents.DEPARTURE_FROM_DRIFTING_IP,
            ReportingEvents.HEAVE_ANCHOR,
            ReportingEvents.NOON_SBE_IP,
            ReportingEvents.BOSP,
            ReportingEvents.DRIFTING_SBE_AS,
            ReportingEvents.DRIFTING_FWE_AS,
            ReportingEvents.DEPARTURE_FROM_DRIFTING_AS,
            ReportingEvents.ROSP,
            ReportingEvents.NOON_FWE_AS,
            ReportingEvents.NOON_SBE_AS,
            ReportingEvents.CII_ADJUSTMENT_AS,
            ReportingEvents.CII_ADJUSTMENT_IP,
        ],
        seaForce: [
            ReportingEvents.NOON_RFA_AS,
            ReportingEvents.EOSP,
            ReportingEvents.HASP,
            ReportingEvents.NOON_FWE_IP,
            ReportingEvents.DROP_ANCHOR,
            ReportingEvents.DRIFTING_FWE_IP,
            ReportingEvents.DRIFTING_SBE_IP,
            ReportingEvents.DEPARTURE_FROM_DRIFTING_IP,
            ReportingEvents.HEAVE_ANCHOR,
            ReportingEvents.NOON_SBE_IP,
            ReportingEvents.BOSP,
            ReportingEvents.DRIFTING_SBE_AS,
            ReportingEvents.DRIFTING_FWE_AS,
            ReportingEvents.DEPARTURE_FROM_DRIFTING_AS,
            ReportingEvents.ROSP,
            ReportingEvents.NOON_FWE_AS,
            ReportingEvents.NOON_SBE_AS,
            ReportingEvents.CII_ADJUSTMENT_AS,
            ReportingEvents.CII_ADJUSTMENT_IP,
        ],
        oceanCurrentDirection: [
            ReportingEvents.NOON_RFA_AS,
            ReportingEvents.EOSP,
            ReportingEvents.HASP,
            ReportingEvents.NOON_FWE_IP,
            ReportingEvents.DROP_ANCHOR,
            ReportingEvents.DRIFTING_FWE_IP,
            ReportingEvents.DRIFTING_SBE_IP,
            ReportingEvents.DEPARTURE_FROM_DRIFTING_IP,
            ReportingEvents.HEAVE_ANCHOR,
            ReportingEvents.NOON_SBE_IP,
            ReportingEvents.BOSP,
            ReportingEvents.DRIFTING_SBE_AS,
            ReportingEvents.DRIFTING_FWE_AS,
            ReportingEvents.DEPARTURE_FROM_DRIFTING_AS,
            ReportingEvents.ROSP,
            ReportingEvents.NOON_FWE_AS,
            ReportingEvents.NOON_SBE_AS,
            ReportingEvents.CII_ADJUSTMENT_AS,
            ReportingEvents.CII_ADJUSTMENT_IP,
        ],
        badWeather: [
            ReportingEvents.NOON_RFA_AS,
            ReportingEvents.EOSP,
            ReportingEvents.HASP,
            ReportingEvents.AT_BERTH,
            ReportingEvents.DEPARTURE_FROM_BERTH,
            ReportingEvents.NOON_FWE_IP,
            ReportingEvents.DROP_ANCHOR,
            ReportingEvents.DRIFTING_FWE_IP,
            ReportingEvents.DRIFTING_SBE_IP,
            ReportingEvents.DEPARTURE_FROM_DRIFTING_IP,
            ReportingEvents.HEAVE_ANCHOR,
            ReportingEvents.NOON_SBE_IP,
            ReportingEvents.BOSP,
            ReportingEvents.DRIFTING_SBE_AS,
            ReportingEvents.DRIFTING_FWE_AS,
            ReportingEvents.DEPARTURE_FROM_DRIFTING_AS,
            ReportingEvents.ROSP,
            ReportingEvents.NOON_FWE_AS,
            ReportingEvents.NOON_SBE_AS,
            ReportingEvents.CII_ADJUSTMENT_AS,
            ReportingEvents.CII_ADJUSTMENT_IP,
        ],
        airTemperature: [
            ReportingEvents.NOON_RFA_AS,
            ReportingEvents.EOSP,
            ReportingEvents.HASP,
            ReportingEvents.AT_BERTH,
            ReportingEvents.DEPARTURE_FROM_BERTH,
            ReportingEvents.NOON_FWE_IP,
            ReportingEvents.DROP_ANCHOR,
            ReportingEvents.DRIFTING_FWE_IP,
            ReportingEvents.DRIFTING_SBE_IP,
            ReportingEvents.DEPARTURE_FROM_DRIFTING_IP,
            ReportingEvents.HEAVE_ANCHOR,
            ReportingEvents.NOON_SBE_IP,
            ReportingEvents.BOSP,
            ReportingEvents.DRIFTING_SBE_AS,
            ReportingEvents.DRIFTING_FWE_AS,
            ReportingEvents.DEPARTURE_FROM_DRIFTING_AS,
            ReportingEvents.ROSP,
            ReportingEvents.NOON_FWE_AS,
            ReportingEvents.NOON_SBE_AS,
            ReportingEvents.CII_ADJUSTMENT_AS,
            ReportingEvents.CII_ADJUSTMENT_IP,
        ],
        seaTemperature: [
            ReportingEvents.NOON_RFA_AS,
            ReportingEvents.EOSP,
            ReportingEvents.HASP,
            ReportingEvents.AT_BERTH,
            ReportingEvents.DEPARTURE_FROM_BERTH,
            ReportingEvents.NOON_FWE_IP,
            ReportingEvents.DROP_ANCHOR,
            ReportingEvents.DRIFTING_FWE_IP,
            ReportingEvents.DRIFTING_SBE_IP,
            ReportingEvents.DEPARTURE_FROM_DRIFTING_IP,
            ReportingEvents.HEAVE_ANCHOR,
            ReportingEvents.NOON_SBE_IP,
            ReportingEvents.BOSP,
            ReportingEvents.DRIFTING_SBE_AS,
            ReportingEvents.DRIFTING_FWE_AS,
            ReportingEvents.DEPARTURE_FROM_DRIFTING_AS,
            ReportingEvents.ROSP,
            ReportingEvents.NOON_FWE_AS,
            ReportingEvents.NOON_SBE_AS,
            ReportingEvents.CII_ADJUSTMENT_AS,
            ReportingEvents.CII_ADJUSTMENT_IP,
        ],
        gyroCourse: [
            ReportingEvents.NOON_RFA_AS,
            ReportingEvents.EOSP,
            ReportingEvents.HASP,
            ReportingEvents.BOSP,
            ReportingEvents.ROSP,
            ReportingEvents.CII_ADJUSTMENT_AS,
        ],
        barometricPressure: [
            ReportingEvents.NOON_RFA_AS,
            ReportingEvents.EOSP,
            ReportingEvents.HASP,
            ReportingEvents.AT_BERTH,
            ReportingEvents.DEPARTURE_FROM_BERTH,
            ReportingEvents.NOON_FWE_IP,
            ReportingEvents.DROP_ANCHOR,
            ReportingEvents.DRIFTING_FWE_IP,
            ReportingEvents.DRIFTING_SBE_IP,
            ReportingEvents.DEPARTURE_FROM_DRIFTING_IP,
            ReportingEvents.HEAVE_ANCHOR,
            ReportingEvents.NOON_SBE_IP,
            ReportingEvents.BOSP,
            ReportingEvents.DRIFTING_SBE_AS,
            ReportingEvents.DRIFTING_FWE_AS,
            ReportingEvents.DEPARTURE_FROM_DRIFTING_AS,
            ReportingEvents.ROSP,
            ReportingEvents.NOON_FWE_AS,
            ReportingEvents.NOON_SBE_AS,
            ReportingEvents.CII_ADJUSTMENT_AS,
            ReportingEvents.CII_ADJUSTMENT_IP,
        ],
    };

    /** Calulates the steaming time of the vessel by calculating the difference between current & last report time */
    const calculateSteamingTime = () => {
        let steamingTime = calculateTimeDurationBetwnReports(
            previousVesselReport?.reporting_time_utc,
            currentVesselReport?.reporting_time_utc
        );
        steamingTime = !isNaN(steamingTime) ? steamingTime : 0;
        if (fieldVisibility.steamingTime.includes(currentVesselReport && currentVesselReport?.reporting_event)) {
            return steamingTime;
        } else {
            return 0;
        }
    };

    /** Calulates the average ground speed of the vessel based on distance travelled since last report*/
    const calculateAvgGroundSpeed = () => {
        let speed: number;
        if (currentVesselReport && previousVesselReport) {
            speed = (currentVesselReport?.distance_travelled * 60) / calculateSteamingTime();
        }
        if (speed && !isNaN(speed) && speed !== Infinity) {
            return speed?.toFixed(2);
        } else {
            return 0;
        }
    }

    /** Queries start */
    const { data: directionOptions, isLoading: LoadingDirection, isError: ErrorDirection } = useQuery(
        [queryKeyes.masters.currentDirection.key],
        async () => await Get(queryKeyes.masters.currentDirection.url()), {
        enabled: true,
        staleTime: Infinity
    });

    const {
        data: generalAvgSpeed,
        isLoading: generalAvgSpeedLoading,
    } = useQuery(
        [queryKeyes.peninsular.PeninsularGeneralAvgSpeedObject.key, VesselID, ReportID],
        () => Get(queryKeyes.peninsular.PeninsularGeneralAvgSpeedObject.url(VesselID, ReportID), VesselID, ReportID),
        {
            enabled: AddGeneralAvgSpeed,
            staleTime: 0,
        }
    );
    /** Queries end */

    /** Assign values to initial object of Weather data */
    const getInitialValues = () => {
        let weatherData = {};
        let additionalInfo = {};
        if (AddGeneralAvgSpeed) {
            if (checkValidArray(generalAvgSpeed)) {
                additionalInfo = {
                    ...generalAvgSpeed[0],
                }
            } else {
                additionalInfo = {
                    general_avg_speed: 0,
                };
            }
        }
        if (editInitialValues && editInitialValues?.deck_officer?.weather_data?.id > 0) {
            weatherData = { ...editInitialValues?.deck_officer?.weather_data };
        } else if (checkValidObject(weatherDataCurrentReport)) {
            weatherData = { ...weatherDataCurrentReport };
        } else {
            weatherData = {
                steaming_time: calculateSteamingTime(),
                distance_travelled: null,
                avg_ground_speed: calculateAvgGroundSpeed(),
                avg_water_speed: null as number,
                wind_direction: null,
                wind_speed: null,
                barometric_pressure: null,
                swell_height: null,
                swell_direction: null,
                ocean_current_speed: null,
                ocean_wave_height: null,
                ocean_current_direction: null,
                sea_force: null,
                beaufort_scale: null,
                air_temperature: null,
                sea_temperature: null,
                period_in_bad_weather: null,
                gyro_course: null,
                vessel: VesselID,
                voyage_information: VoyageID,
                vessel_reporting_information: ReportID,
            };
        }
        // Return the parent object with weather_data and additional_info
        if (AddGeneralAvgSpeed) {
            return {
                weather_data: weatherData,
                additional_info: additionalInfo
            };
        } else {
            return {
                weather_data: weatherData,
            };
        }
    };

    /** Weather Data's formik object */
    const WeatherDataFormik = {
        initialValues: getInitialValues(),
        validationSchema: Yup.object({
            weather_data: Yup.object({
                avg_ground_speed: Yup.number().nullable(),
                wind_direction: Yup.number().when(
                    (_value: any, schema) => {
                        return !fieldVisibility.windDirection?.includes(currentVesselReport?.reporting_event)
                            ? schema.nullable()
                            : schema.required(commonValidationMessages.required);
                    }
                ),
                avg_water_speed: Yup.string().when(
                    (_value: any, schema) => {
                        return !fieldVisibility?.avgWaterSpeed?.includes(currentVesselReport?.reporting_event)
                            ? schema.nullable()
                            : schema
                                .matches(/^[0-9]{0,2}(\.[0-9]{1,4})?$/, commonValidationMessages.before2after4)
                                .required(commonValidationMessages.required)
                    }
                ),
                wind_speed: Yup.number()
                    .typeError(commonValidationMessages.number)
                    .max(450, 'Please enter a number less than or equal to 450')
                    .test(
                        'decimal-precision',
                        commonValidationMessages.before3after4,
                        (value) => {
                            if (fieldVisibility.windSpeed?.includes(currentVesselReport?.reporting_event)) {
                                return /^[0-9]{0,3}(\.[0-9]{1,4})?$/.test(value.toString());
                            }
                            if (value === null) return true;
                        }
                    ).when(
                        (_value: any, schema) => {
                            return !fieldVisibility.windSpeed?.includes(currentVesselReport?.reporting_event)
                                ? schema.nullable()
                                : schema.required(commonValidationMessages.required);
                        }),
                beaufort_scale: Yup.number().nullable(),
                swell_direction: Yup.number().when(
                    (_value: any, schema) => {
                        return !fieldVisibility.swellDirection?.includes(currentVesselReport?.reporting_event)
                            ? schema.nullable()
                            : schema.required(commonValidationMessages.required)
                    }
                ),
                swell_height: Yup.string().when(
                    (_value: any, schema) => {
                        return !fieldVisibility.swellHeight?.includes(currentVesselReport?.reporting_event)
                            ? schema.nullable()
                            : schema
                                .matches(/^[0-9]{0,2}(\.[0-9]{1,4})?$/, commonValidationMessages.before2after4)
                                .required(commonValidationMessages.required);
                    }),
                ocean_current_speed: Yup.string().when(
                    (_value: any, schema) => {
                        return !fieldVisibility.oceanCurrentSpeed?.includes(
                            currentVesselReport?.reporting_event
                        )
                            ? schema.nullable()
                            : schema
                                .matches(/^[0-9]{0,2}(\.[0-9]{1,4})?$/, commonValidationMessages.before2after4)
                                .required(commonValidationMessages.required);
                    }),
                ocean_wave_height: Yup.string().when(
                    (_value: any, schema) => {
                        return !fieldVisibility.oceanWaveHeight?.includes(
                            currentVesselReport?.reporting_event
                        )
                            ? schema.nullable()
                            : schema
                                .matches(/^[0-9]{1,2}(\.[0-9]{1,4})?$/, commonValidationMessages.before2after4)
                                .required(commonValidationMessages.required);
                    }),
                sea_force: Yup.number().nullable(),
                ocean_current_direction: Yup.number().when(
                    (_value: any, schema) => {
                        return !fieldVisibility.oceanCurrentDirection?.includes(
                            currentVesselReport?.reporting_event
                        )
                            ? schema.nullable()
                            : schema.required(commonValidationMessages.required);
                    }
                ),
                period_in_bad_weather: Yup.string().when(
                    (_value: any, schema) => {
                        return !fieldVisibility.badWeather?.includes(currentVesselReport?.reporting_event)
                            ? schema.nullable()
                            : schema
                                .matches(/^[0-9]{0,2}(\.[0-9]{1,4})?$/, commonValidationMessages.before2after4)
                                .required(commonValidationMessages.required);
                    }),
                air_temperature: Yup.string().when((_value: any, schema) => {
                    return !fieldVisibility.airTemperature?.includes(currentVesselReport?.reporting_event)
                        ? schema.nullable()
                        : schema
                            .matches(/^[-]?\d{0,2}(\.\d{1,2})?$/, commonValidationMessages.before2after2)
                            .matches(/^[-]?(?:\d+(?:\.\d{1,2})?)$/, commonValidationMessages.validTemperature)
                            .required(commonValidationMessages.required);
                }),
                sea_temperature: Yup.string().when((_value: any, schema) => {
                    return !fieldVisibility.seaTemperature?.includes(currentVesselReport?.reporting_event)
                        ? schema.nullable()
                        : schema
                            .matches(
                                /^[-]?(?:\d+(?:\.\d{1,4})?)$/,
                                commonValidationMessages.validTemperature // Show general structure error first
                            )
                            .matches(
                                /^[-]?\d{1,2}(\.\d{1,4})?$/,
                                commonValidationMessages.before2after4 // Show precision-specific error after
                            )
                            .required(commonValidationMessages.required);
                }),
                gyro_course: Yup.string().when(
                    (_value: any, schema) => {
                        return !fieldVisibility.gyroCourse?.includes(currentVesselReport?.reporting_event)
                            ? schema.nullable()
                            : schema
                                .matches(/^(?:\d{1,2}|[1-2]\d{2}|3[0-5]\d|360)$/, WeatherDataValidationMessages.gyro_course)
                                .required(commonValidationMessages.required);
                    }),
                barometric_pressure: Yup.number()
                    .typeError(commonValidationMessages.number)
                    .min(850, 'Value should be in range of 850 to 1100')
                    .max(1100, 'Value should be in range of 850 to 1100')
                    .test(
                        'decimal-precision',
                        commonValidationMessages.before3after4,
                        (value) => {
                            if (fieldVisibility.barometricPressure?.includes(currentVesselReport?.reporting_event)) {
                                return /^\d{3,4}(\.\d{1,3})?$/.test(value.toString());
                            }
                            if (value === null) return true;
                        }
                    ).when(
                        (_value: any, schema) => {
                            return !fieldVisibility.barometricPressure?.includes(currentVesselReport?.reporting_event)
                                ? schema.nullable()
                                : schema.required(commonValidationMessages.required);
                        }),
            }),
            additional_info: Yup.object({
                general_avg_speed: Yup.string()
                    .matches(/^[0-9]{0,2}(\.[0-9]{1,4})?$/, commonValidationMessages.before2after4)
                    .when((_field: any, schema) => {
                        return fieldVisibility.generalAvgSpeed?.includes(currentVesselReport?.reporting_event) && AddGeneralAvgSpeed
                            ? schema.required(commonValidationMessages.required)
                            : schema.nullable()
                    }),
            })
        }),
    };

    /** UseEffect */
    useEffect(() => {
        WeatherDataFormik.initialValues = getInitialValues();
        setRefreshKey(refreshKey + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [weatherDataCurrentReport, previousVesselReport, currentVesselReport]);
    /** UseEffect end */

    const calculateBeaufortScale = (e: any, setFieldValue: any) => {
        let value = parseInt(e.target.value);
        let beaufortScale = 0;
        if (parseInt(e.target.value) === 0) {
            beaufortScale = 0;
        } else if (1 <= value && value <= 3) {
            beaufortScale = 1;
        } else if (4 <= value && value <= 6) {
            beaufortScale = 2;
        } else if (7 <= value && value <= 10) {
            beaufortScale = 3;
        } else if (11 <= value && value <= 16) {
            beaufortScale = 4;
        } else if (17 <= value && value <= 21) {
            beaufortScale = 5;
        } else if (22 <= value && value <= 27) {
            beaufortScale = 6;
        } else if (28 <= value && value <= 33) {
            beaufortScale = 7;
        } else if (34 <= value && value <= 40) {
            beaufortScale = 8;
        } else if (41 <= value && value <= 47) {
            beaufortScale = 9;
        } else if (48 <= value && value <= 55) {
            beaufortScale = 10;
        } else if (56 <= value && value <= 63) {
            beaufortScale = 11;
        } else if (64 <= value && value <= 999) {
            beaufortScale = 12;
        }
        setFieldValue("weather_data.beaufort_scale", beaufortScale);
    };

    const calculateSeaForce = (e: any, setFieldValue: any) => {
        let value = parseFloat(e.target.value);
        let seaForce = 0;
        if (value < 0) {
            seaForce = 0;
        } else if (0 <= value && value <= 0.1) {
            seaForce = 1;
        } else if (0.11 <= value && value <= 0.5) {
            seaForce = 2;
        } else if (0.51 <= value && value <= 1.25) {
            seaForce = 3;
        } else if (1.26 <= value && value <= 2.5) {
            seaForce = 4;
        } else if (2.51 <= value && value <= 4.0) {
            seaForce = 5;
        } else if (4.01 <= value && value <= 6.0) {
            seaForce = 6;
        } else if (6.01 <= value && value <= 9.0) {
            seaForce = 7;
        } else if (9.01 <= value && value <= 14.0) {
            seaForce = 8;
        } else if (14.0 < value) {
            seaForce = 9;
        }
        setFieldValue("weather_data.sea_force", seaForce);
    };

    return (
        <Card className="p-0 mb-0 border-0">
            <CardHeader className="p-2">
                <div className="text-center">
                    <Row>
                        <Col>
                            <h4 className="page_title pos-start mb-0">Weather Data</h4>
                            <p className="card-title-desc pos-start">
                                All readings since last report
                            </p>
                        </Col>
                        <Col className="align-middle">
                            <ReportDetailsHeader />
                        </Col>
                    </Row>
                </div>
            </CardHeader>
            {(reportingLoading || LoadingDirection) && <FormLoading />}
            {(ErrorDirection || reportingError) && <ErrorComponent message="Unable to load required data!" />}
            {(!(reportingLoading || LoadingDirection || generalAvgSpeedLoading) &&
                !(ErrorDirection || reportingError) &&
                <Formik
                    onSubmit={async (values: any, actions: any) => {
                        const responseArray: any = [];

                        if (env?.form_validation === false || isFormUnchanged(WeatherDataFormik.initialValues, values)) {
                            setErrorMessage(null);
                            toggleTab(1);
                            return;
                        }

                        actions.setSubmitting(true);
                        if (checkValidObject(weatherDataCurrentReport) && values?.weather_data?.id) {
                            responseArray.push(apiGlobal
                                .put(`weather_data/${values?.weather_data?.id}/`, values?.weather_data))
                        } else {
                            responseArray.push(apiGlobal
                                .post(`weather_data/`, values?.weather_data))
                        }
                        if (AddGeneralAvgSpeed && fieldVisibility.generalAvgSpeed?.includes(currentVesselReport?.reporting_event)) {
                            const speedValue = values?.additional_info?.general_avg_speed;

                            if (speedValue !== null && speedValue !== undefined) {
                                const payload = {
                                    general_avg_speed: speedValue,
                                    vessel_reporting_information: ReportID,
                                    voyage_information: VoyageID,
                                    vessel: VesselID,
                                };
                                if (generalAvgSpeed && generalAvgSpeed.length > 0 && generalAvgSpeed[0]?.id) {
                                    // PUT request if data exists
                                    responseArray.push(
                                        apiGlobal.put(`peninsular_deck_additional/${generalAvgSpeed[0].id}/`, payload)
                                    );
                                } else {
                                    // POST request if no existing data
                                    responseArray.push(
                                        apiGlobal.post(`peninsular_deck_additional/`, payload)
                                    );
                                }
                            }
                        }
                        await handleServerResponse(responseArray).then(async (res) => {
                            if (res === true) {
                                await dispatch(getCurrentReportAction(VesselID, ReportID));
                                await dispatch(getCurrentReportFilesAction(ReportID, VesselID));
                                queryClient.invalidateQueries(queryKeyes.peninsular.PeninsularGeneralAvgSpeedObject.key);
                                if (env?.form_validation === true) {
                                    setErrorMessage(null)
                                    toggleTab(1);
                                }
                            } else {
                                setErrorMessage(res)
                                setAlertBool(false)
                                dynamicSetFieldErrors(res, actions, false)
                            }
                            actions.setSubmitting(false);
                        })
                        actions.setSubmitting(false);
                    }}
                    initialValues={WeatherDataFormik.initialValues}
                    validationSchema={env?.form_validation === true ? WeatherDataFormik.validationSchema : null}
                    key={refreshKey}
                >
                    {() => (
                        <WeatherDataStructure
                            refreshKey={refreshKey}
                            toggleTab={toggleTab}
                            alertBool={alertBool}
                            setAlertBool={setAlertBool}
                            setAlertColourType={setAlertColourType}
                            setAlertType={setAlertType}
                            alertMessage={alertMessage}
                            setAlertMessage={setAlertMessage}
                            fieldId={fieldId}
                            setFieldId={setFieldId}
                            fieldVisibility={fieldVisibility}
                            setErrorMessage={setErrorMessage}
                            setRefreshKey={setRefreshKey}
                            AddGeneralAvgSpeed={AddGeneralAvgSpeed}
                            directionOptions={directionOptions}
                            calculateSeaForce={calculateSeaForce}
                            calculateBeaufortScale={calculateBeaufortScale}
                            alertType={alertType}
                            alertColourType={alertColourType}
                            editValues={editValues}
                            setEditValues={setEditValues}
                            editInitalValues={editInitialValues}
                            editSubmit={editSubmit}
                            editIsSubmitting={editIsSubmitting}
                            fieldValue={fieldValue}
                            setAlertFieldValue={setAlertFieldValue}
                            fieldLable={fieldLabel}
                            setAlertFieldLabel={setAlertFieldLabel}
                        />
                    )}
                </Formik>
            )
            }
        </Card >
    );
};

export default WeatherData;